import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Leaf,
    BookOpen,
    Article,
    Question,
    ArrowLeft,
    House,
    Target,
    Binoculars,
    Trophy,
    Clock,
    Star,
    Lightning,
    Sparkle,
    X,
    ArrowRight
} from '@phosphor-icons/react';

export default function ContentHubPage() {
    const { user, api, updateUser, isChild } = useAuth();
    const [activeTab, setActiveTab] = useState('articles');
    const [articles, setArticles] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizScore, setQuizScore] = useState(null);
    
    // Article modal
    const [selectedArticle, setSelectedArticle] = useState(null);
    
    // Instant Quiz Maker (for children)
    const [showQuizMaker, setShowQuizMaker] = useState(false);
    const [quizTopic, setQuizTopic] = useState('');
    const [generatingQuiz, setGeneratingQuiz] = useState(false);
    const [instantQuiz, setInstantQuiz] = useState(null);
    const [instantQuizAnswers, setInstantQuizAnswers] = useState({});
    const [instantQuizSubmitted, setInstantQuizSubmitted] = useState(false);
    const [instantQuizScore, setInstantQuizScore] = useState(null);

    useEffect(() => {
        fetchContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchContent = async () => {
        try {
            const [articlesRes, quizzesRes] = await Promise.all([
                api().get('/articles'),
                api().get('/quizzes')
            ]);
            setArticles(articlesRes.data);
            setQuizzes(quizzesRes.data);
        } catch (error) {
            toast.error('Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    const handleAnswerSelect = (questionIndex, answerIndex) => {
        setQuizAnswers(prev => ({
            ...prev,
            [questionIndex]: answerIndex
        }));
    };

    const handleSubmitQuiz = async () => {
        if (!activeQuiz) return;
        
        let correct = 0;
        activeQuiz.questions.forEach((q, i) => {
            if (quizAnswers[i] === q.correct) correct++;
        });
        
        const score = Math.round((correct / activeQuiz.questions.length) * 100);
        setQuizScore(score);
        setQuizSubmitted(true);

        if (isChild) {
            try {
                const response = await api().post(`/quizzes/${activeQuiz.id}/complete?score=${score}`);
                updateUser({
                    xp: response.data.new_xp,
                    level: response.data.new_level
                });
                toast.success(`You earned ${response.data.xp_earned} Bush Tokens!`);
            } catch (error) {
                console.error('Quiz completion error:', error);
            }
        }
    };

    const resetQuiz = () => {
        setActiveQuiz(null);
        setQuizAnswers({});
        setQuizSubmitted(false);
        setQuizScore(null);
    };

    // Instant Quiz Functions
    const handleGenerateInstantQuiz = async () => {
        if (!quizTopic.trim()) {
            toast.error('Please enter a topic!');
            return;
        }
        
        setGeneratingQuiz(true);
        try {
            const response = await api().post(`/ai/generate-quiz-child?topic=${encodeURIComponent(quizTopic)}`);
            setInstantQuiz(response.data);
            setShowQuizMaker(false);
            setQuizTopic('');
        } catch (error) {
            toast.error('Failed to create quiz. Try again!');
        } finally {
            setGeneratingQuiz(false);
        }
    };

    const handleInstantQuizAnswer = (questionIndex, answerIndex) => {
        setInstantQuizAnswers(prev => ({
            ...prev,
            [questionIndex]: answerIndex
        }));
    };

    const handleSubmitInstantQuiz = async () => {
        if (!instantQuiz) return;
        
        let correct = 0;
        instantQuiz.questions.forEach((q, i) => {
            if (instantQuizAnswers[i] === q.correct) correct++;
        });
        
        const score = Math.round((correct / instantQuiz.questions.length) * 100);
        setInstantQuizScore(score);
        setInstantQuizSubmitted(true);

        // Award XP for instant quiz
        if (isChild && instantQuiz.xp_reward) {
            try {
                const xpEarned = Math.round(instantQuiz.xp_reward * (score / 100));
                // Update user XP locally (instant quizzes don't persist)
                const newXp = (user?.xp || 0) + xpEarned;
                updateUser({ xp: newXp });
                toast.success(`You earned ${xpEarned} Bush Tokens!`);
            } catch (error) {
                console.error('XP update error:', error);
            }
        }
    };

    const closeInstantQuiz = () => {
        setInstantQuiz(null);
        setInstantQuizAnswers({});
        setInstantQuizSubmitted(false);
        setInstantQuizScore(null);
    };

    const suggestedTopics = [
        "Koalas", "Great Barrier Reef", "Kangaroos", "Recycling", 
        "Bushfires", "Ocean pollution", "Rainforests", "Endangered animals"
    ];

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/dashboard"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#1e293b] mb-2">
                        <BookOpen weight="duotone" className="inline text-[#38bdf8] mr-2" size={40} />
                        Learn & Explore
                    </h1>
                    <p className="text-[#475569] text-lg">
                        Discover amazing facts about Australian wildlife and environment!
                    </p>
                </motion.div>

                {/* Instant Quiz Maker Button (for children) */}
                {isChild && (
                    <motion.div 
                        className="mb-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                    >
                        <button
                            onClick={() => setShowQuizMaker(true)}
                            className="w-full brutalist-card p-4 bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white flex items-center justify-between hover:shadow-lg transition-shadow"
                            data-testid="instant-quiz-btn"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                    <Sparkle weight="fill" size={28} />
                                </div>
                                <div className="text-left">
                                    <p className="font-fredoka font-bold text-lg">Instant Quiz Maker</p>
                                    <p className="text-white/80 text-sm">Create your own quiz on any topic!</p>
                                </div>
                            </div>
                            <ArrowRight weight="bold" size={24} />
                        </button>
                    </motion.div>
                )}

                {/* Tabs */}
                <motion.div 
                    className="flex gap-3 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <button
                        onClick={() => { setActiveTab('articles'); resetQuiz(); }}
                        className={`px-6 py-3 rounded-full font-fredoka font-semibold flex items-center gap-2 transition-all ${
                            activeTab === 'articles'
                                ? 'bg-[#38bdf8] text-white border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#38bdf8]'
                        }`}
                        data-testid="tab-articles"
                    >
                        <Article weight="duotone" size={20} />
                        Articles
                    </button>
                    <button
                        onClick={() => { setActiveTab('quizzes'); resetQuiz(); }}
                        className={`px-6 py-3 rounded-full font-fredoka font-semibold flex items-center gap-2 transition-all ${
                            activeTab === 'quizzes'
                                ? 'bg-[#fde047] text-[#1e293b] border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]'
                                : 'bg-white text-[#1e293b] border-2 border-[#1e293b]/20 hover:border-[#fde047]'
                        }`}
                        data-testid="tab-quizzes"
                    >
                        <Question weight="duotone" size={20} />
                        Quizzes
                    </button>
                </motion.div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="font-fredoka text-lg">Loading content...</p>
                    </div>
                ) : (
                    <>
                        {/* Articles Tab */}
                        {activeTab === 'articles' && (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {articles.length === 0 ? (
                                    <div className="col-span-full brutalist-card p-8 text-center">
                                        <Article weight="duotone" className="text-[#475569] mx-auto mb-4" size={48} />
                                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                                            No articles yet
                                        </h3>
                                        <p className="text-[#475569]">
                                            Check back soon for exciting content!
                                        </p>
                                    </div>
                                ) : (
                                    articles.map((article, index) => (
                                        <motion.div
                                            key={article.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.05 * index }}
                                            className="brutalist-card overflow-hidden h-full cursor-pointer hover:shadow-lg transition-shadow"
                                            onClick={() => setSelectedArticle(article)}
                                            data-testid={`article-card-${article.id}`}
                                        >
                                            {article.image_url && (
                                                <div className="h-40 overflow-hidden">
                                                    <img 
                                                        src={article.image_url} 
                                                        alt={article.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="p-5">
                                                <span className="px-2 py-1 bg-[#38bdf8]/20 rounded-lg text-xs font-semibold text-[#38bdf8] capitalize">
                                                    {article.category}
                                                </span>
                                                <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mt-2 mb-2">
                                                    {article.title}
                                                </h3>
                                                <p className="text-sm text-[#475569] line-clamp-3 mb-3">
                                                    {article.content}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 text-[#475569] text-sm">
                                                        <Clock weight="duotone" size={16} />
                                                        <span>{article.reading_time} min read</span>
                                                    </div>
                                                    <span className="text-[#38bdf8] font-semibold text-sm">Read More →</span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Quizzes Tab */}
                        {activeTab === 'quizzes' && !activeQuiz && (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {quizzes.length === 0 ? (
                                    <div className="col-span-full brutalist-card p-8 text-center">
                                        <Question weight="duotone" className="text-[#475569] mx-auto mb-4" size={48} />
                                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">
                                            No quizzes yet
                                        </h3>
                                        <p className="text-[#475569]">
                                            Check back soon for fun quizzes!
                                        </p>
                                    </div>
                                ) : (
                                    quizzes.map((quiz, index) => (
                                        <motion.div
                                            key={quiz.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.05 * index }}
                                            className="brutalist-card p-6 bg-gradient-to-br from-[#fde047]/50 to-[#fde047]/20"
                                            data-testid={`quiz-card-${quiz.id}`}
                                        >
                                            <div className="w-16 h-16 rounded-2xl bg-[#fde047] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                                                <Question weight="fill" className="text-[#1e293b]" size={32} />
                                            </div>
                                            <h3 className="font-fredoka font-bold text-xl text-[#1e293b] mb-2">
                                                {quiz.title}
                                            </h3>
                                            <p className="text-sm text-[#475569] mb-4">
                                                {quiz.questions.length} questions
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-1">
                                                    <Star weight="fill" className="text-[#fde047]" size={20} />
                                                    <span className="font-fredoka font-bold">+{quiz.xp_reward} Tokens</span>
                                                </div>
                                                <motion.button
                                                    onClick={() => setActiveQuiz(quiz)}
                                                    whileHover={{ y: -2, x: -2 }}
                                                    whileTap={{ y: 2, x: 2 }}
                                                    className="brutalist-btn bg-[#22c55e] text-white text-sm py-2"
                                                >
                                                    Start Quiz
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* Active Quiz */}
                        {activeTab === 'quizzes' && activeQuiz && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="max-w-2xl mx-auto"
                            >
                                <div className="brutalist-card p-6 md:p-8">
                                    <div className="flex justify-between items-center mb-6">
                                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                            {activeQuiz.title}
                                        </h2>
                                        <button
                                            onClick={resetQuiz}
                                            className="text-[#475569] hover:text-[#1e293b] font-semibold"
                                        >
                                            ← Back
                                        </button>
                                    </div>

                                    {!quizSubmitted ? (
                                        <>
                                            <div className="space-y-6">
                                                {activeQuiz.questions.map((question, qIndex) => (
                                                    <div key={qIndex} className="bg-[#f1f5f9] rounded-xl p-5">
                                                        <p className="font-fredoka font-bold text-[#1e293b] mb-4">
                                                            {qIndex + 1}. {question.question}
                                                        </p>
                                                        <div className="space-y-2">
                                                            {question.options.map((option, oIndex) => (
                                                                <button
                                                                    key={oIndex}
                                                                    onClick={() => handleAnswerSelect(qIndex, oIndex)}
                                                                    className={`w-full text-left p-4 rounded-xl border-4 transition-all ${
                                                                        quizAnswers[qIndex] === oIndex
                                                                            ? 'border-[#22c55e] bg-[#22c55e]/10'
                                                                            : 'border-[#1e293b]/20 hover:border-[#22c55e]/50 bg-white'
                                                                    }`}
                                                                    data-testid={`question-${qIndex}-option-${oIndex}`}
                                                                >
                                                                    <span className="font-semibold">{option}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <motion.button
                                                onClick={handleSubmitQuiz}
                                                disabled={Object.keys(quizAnswers).length < activeQuiz.questions.length}
                                                whileHover={{ y: -2, x: -2 }}
                                                whileTap={{ y: 2, x: 2 }}
                                                className="brutalist-btn w-full mt-6 bg-[#22c55e] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="submit-quiz"
                                            >
                                                Submit Quiz
                                            </motion.button>
                                        </>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 border-4 border-[#1e293b] ${
                                                quizScore >= 80 ? 'bg-[#22c55e]' :
                                                quizScore >= 50 ? 'bg-[#fde047]' :
                                                'bg-[#fb7185]'
                                            }`}>
                                                <span className="font-fredoka text-3xl font-bold text-white">
                                                    {quizScore}%
                                                </span>
                                            </div>
                                            <h3 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">
                                                {quizScore >= 80 ? '🎉 Amazing!' :
                                                 quizScore >= 50 ? '👍 Good job!' :
                                                 '💪 Keep trying!'}
                                            </h3>
                                            <p className="text-[#475569] mb-6">
                                                You got {Math.round((quizScore / 100) * activeQuiz.questions.length)} out of {activeQuiz.questions.length} correct!
                                            </p>

                                            <div className="text-left space-y-4 mb-6">
                                                {activeQuiz.questions.map((q, i) => (
                                                    <div key={i} className={`p-4 rounded-xl ${
                                                        quizAnswers[i] === q.correct ? 'bg-[#22c55e]/10' : 'bg-[#fb7185]/10'
                                                    }`}>
                                                        <p className="font-semibold text-[#1e293b] mb-1">
                                                            {i + 1}. {q.question}
                                                        </p>
                                                        <p className="text-sm">
                                                            Your answer: <span className={quizAnswers[i] === q.correct ? 'text-[#22c55e]' : 'text-[#fb7185]'}>
                                                                {q.options[quizAnswers[i]]}
                                                            </span>
                                                            {quizAnswers[i] !== q.correct && (
                                                                <span className="text-[#22c55e]"> (Correct: {q.options[q.correct]})</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>

                                            <motion.button
                                                onClick={resetQuiz}
                                                whileHover={{ y: -2, x: -2 }}
                                                whileTap={{ y: 2, x: 2 }}
                                                className="brutalist-btn bg-[#38bdf8] text-white"
                                            >
                                                Back to Quizzes
                                            </motion.button>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </>
                )}
            </main>

            {/* Article Modal */}
            {selectedArticle && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 md:p-8 w-full max-w-2xl bg-white my-8"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <span className="px-3 py-1 bg-[#38bdf8]/20 rounded-lg text-sm font-semibold text-[#38bdf8] capitalize">
                                {selectedArticle.category}
                            </span>
                            <button 
                                onClick={() => setSelectedArticle(null)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X weight="bold" size={24} />
                            </button>
                        </div>
                        
                        {selectedArticle.image_url && (
                            <div className="h-48 md:h-64 rounded-xl overflow-hidden mb-6 border-4 border-[#1e293b]">
                                <img 
                                    src={selectedArticle.image_url} 
                                    alt={selectedArticle.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                        
                        <h2 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b] mb-4">
                            {selectedArticle.title}
                        </h2>
                        
                        <div className="flex items-center gap-4 text-[#475569] text-sm mb-6">
                            <div className="flex items-center gap-1">
                                <Clock weight="duotone" size={16} />
                                <span>{selectedArticle.reading_time} min read</span>
                            </div>
                        </div>
                        
                        <div className="prose prose-lg text-[#1e293b] leading-relaxed whitespace-pre-wrap">
                            {selectedArticle.content}
                        </div>
                        
                        <motion.button
                            onClick={() => setSelectedArticle(null)}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full mt-6 bg-[#38bdf8] text-white"
                        >
                            Close Article
                        </motion.button>
                    </motion.div>
                </div>
            )}

            {/* Instant Quiz Maker Modal */}
            {showQuizMaker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 w-full max-w-md bg-white"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] flex items-center gap-2">
                                <Sparkle weight="fill" className="text-[#8b5cf6]" />
                                Instant Quiz
                            </h2>
                            <button 
                                onClick={() => setShowQuizMaker(false)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X weight="bold" size={24} />
                            </button>
                        </div>
                        
                        <p className="text-[#475569] mb-4">
                            What do you want to learn about? AI will create a quiz just for you!
                        </p>

                        <input
                            type="text"
                            value={quizTopic}
                            onChange={(e) => setQuizTopic(e.target.value)}
                            placeholder="Enter a topic (e.g., Koalas, Ocean, Recycling)"
                            className="brutalist-input w-full mb-4"
                            data-testid="quiz-topic-input"
                        />
                        
                        <p className="text-sm text-[#475569] mb-2">Or try one of these:</p>
                        <div className="flex flex-wrap gap-2 mb-6">
                            {suggestedTopics.map((topic) => (
                                <button
                                    key={topic}
                                    onClick={() => setQuizTopic(topic)}
                                    className="px-3 py-1 bg-[#f1f5f9] rounded-full text-sm font-semibold text-[#475569] hover:bg-[#8b5cf6]/20 hover:text-[#8b5cf6] transition-colors"
                                >
                                    {topic}
                                </button>
                            ))}
                        </div>

                        <motion.button
                            onClick={handleGenerateInstantQuiz}
                            disabled={generatingQuiz || !quizTopic.trim()}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white disabled:opacity-50"
                            data-testid="generate-quiz-btn"
                        >
                            {generatingQuiz ? (
                                <span className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Creating Quiz...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <Lightning weight="fill" size={20} />
                                    Create My Quiz
                                </span>
                            )}
                        </motion.button>
                    </motion.div>
                </div>
            )}

            {/* Instant Quiz Playing Modal */}
            {instantQuiz && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 md:p-8 w-full max-w-2xl bg-white my-8"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <span className="px-3 py-1 bg-[#8b5cf6]/20 rounded-full text-sm font-semibold text-[#8b5cf6] mb-2 inline-block">
                                    ✨ Instant Quiz
                                </span>
                                <h2 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                                    {instantQuiz.title}
                                </h2>
                            </div>
                            <button 
                                onClick={closeInstantQuiz}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X weight="bold" size={24} />
                            </button>
                        </div>

                        {!instantQuizSubmitted ? (
                            <>
                                <div className="space-y-6">
                                    {instantQuiz.questions.map((question, qIndex) => (
                                        <div key={qIndex} className="bg-[#f1f5f9] rounded-xl p-5">
                                            <p className="font-fredoka font-bold text-[#1e293b] mb-4">
                                                {qIndex + 1}. {question.question}
                                            </p>
                                            <div className="space-y-2">
                                                {question.options.map((option, oIndex) => (
                                                    <button
                                                        key={oIndex}
                                                        onClick={() => handleInstantQuizAnswer(qIndex, oIndex)}
                                                        className={`w-full text-left p-4 rounded-xl border-4 transition-all ${
                                                            instantQuizAnswers[qIndex] === oIndex
                                                                ? 'border-[#8b5cf6] bg-[#8b5cf6]/10'
                                                                : 'border-[#1e293b]/20 hover:border-[#8b5cf6]/50 bg-white'
                                                        }`}
                                                    >
                                                        <span className="font-semibold">{option}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <motion.button
                                    onClick={handleSubmitInstantQuiz}
                                    disabled={Object.keys(instantQuizAnswers).length < instantQuiz.questions.length}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn w-full mt-6 bg-[#8b5cf6] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Submit Quiz
                                </motion.button>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 border-4 border-[#1e293b] ${
                                    instantQuizScore >= 80 ? 'bg-[#22c55e]' :
                                    instantQuizScore >= 50 ? 'bg-[#fde047]' :
                                    'bg-[#fb7185]'
                                }`}>
                                    <span className="font-fredoka text-3xl font-bold text-white">
                                        {instantQuizScore}%
                                    </span>
                                </div>
                                <h3 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">
                                    {instantQuizScore >= 80 ? '🎉 Amazing!' :
                                     instantQuizScore >= 50 ? '👍 Good job!' :
                                     '💪 Keep trying!'}
                                </h3>
                                <p className="text-[#475569] mb-6">
                                    You got {Math.round((instantQuizScore / 100) * instantQuiz.questions.length)} out of {instantQuiz.questions.length} correct!
                                </p>

                                <div className="text-left space-y-4 mb-6">
                                    {instantQuiz.questions.map((q, i) => (
                                        <div key={i} className={`p-4 rounded-xl ${
                                            instantQuizAnswers[i] === q.correct ? 'bg-[#22c55e]/10' : 'bg-[#fb7185]/10'
                                        }`}>
                                            <p className="font-semibold text-[#1e293b] mb-1">
                                                {i + 1}. {q.question}
                                            </p>
                                            <p className="text-sm">
                                                Your answer: <span className={instantQuizAnswers[i] === q.correct ? 'text-[#22c55e]' : 'text-[#fb7185]'}>
                                                    {q.options[instantQuizAnswers[i]]}
                                                </span>
                                                {instantQuizAnswers[i] !== q.correct && (
                                                    <span className="text-[#22c55e]"> (Correct: {q.options[q.correct]})</span>
                                                )}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <motion.button
                                    onClick={closeInstantQuiz}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn bg-[#8b5cf6] text-white"
                                >
                                    Done
                                </motion.button>
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
        </div>
    );
}
